import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { styled } from '../../../lib/stitches'
import { useTreeState } from '../../../stores/Root'
import { Column, Row } from '../../core/Components'
import {
  NextIcon,
  PauseIcon,
  PlayIcon,
  PreviousIcon,
  RepeatIcon,
  ReplayIcon,
  ShuffleIcon
} from '../../core/icons/icons'
import withTooltip from '../../core/withTooltip'
import { gray1, logoBlue } from '../../platform/color'
import { appear } from '../../player/animations'
import { usePlaybackControls } from '../../player/sample/SamplePlaybackControls'
import SampleTimelineControls from '../../player/sample/SampleTimelineControls'
import { PlayerSection } from './Common'

const BUTTON_COLOR = '#22272F'

const ControlsContainer = styled(Column, {
  width: '100%',
  maxWidth: '450px',
  height: '100%',
  padding: '16px 0px'
})

const StyledPlayPauseButton = styled(Row, {
  width: '32px',
  height: '32px',
  minWidth: '32px',
  backgroundColor: '$gray14',
  borderRadius: '50%',
  animation: `${appear} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
  justifyContent: 'center',
  alignItems: 'center',
  transition: 'all 150ms ease',
  '&:hover': {
    opacity: '.9'
  },
  '&:active': {
    opacity: '1',
    transform: 'scale(.95)'
  },
  '&:disabled': {
    opacity: '.4'
  },
  color: gray1,
  variants: {
    marginRight: {
      true: {
        marginRight: '16px'
      }
    }
  }
})

const StyledButton = withTooltip(
  styled(Row, {
    width: '20px',
    height: '20px',
    color: BUTTON_COLOR,
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'all 150ms ease',
    '&:hover': { opacity: '70%' },
    variants: {
      isToggled: {
        true: {
          color: logoBlue
        }
      },
      marginLeft: {
        true: {
          marginLeft: '16px'
        }
      },
      marginRight: {
        true: {
          marginRight: '16px'
        }
      },
      disabled: {
        true: {
          opacity: '50%'
        }
      }
    }
  })
)

const segmentGlobalPlayer = (button) => {
  window.analytics.track('Button Clicked', {
    button: button,
    source: window.location.pathname,
    location: 'global player'
  })
}

const PlayPauseButton = (props) => (
  <StyledPlayPauseButton {...props}>
    {props.isPlaying ? <PauseIcon /> : <PlayIcon />}
  </StyledPlayPauseButton>
)

const AutoplayButton = (props) => {
  const { onClick, isAutoplay, ...other } = props
  return (
    <StyledButton
      onClick={onClick}
      isToggled={isAutoplay}
      tooltipText={isAutoplay ? 'Disable autoplay' : 'Enable autoplay'}
      {...other}
    >
      <ReplayIcon />
    </StyledButton>
  )
}

const ShuffleButton = (props) => {
  const { onClick, isShuffle, ...other } = props
  return (
    <StyledButton
      onClick={onClick}
      isToggled={isShuffle}
      tooltipText={isShuffle ? 'Disable shuffle' : 'Enable shuffle'}
      {...other}
    >
      <ShuffleIcon />
    </StyledButton>
  )
}

const LoopButton = (props) => {
  const { onClick, isRepeat, ...other } = props
  return (
    <StyledButton
      onClick={onClick}
      isToggled={isRepeat}
      tooltipText={isRepeat ? 'Disable repeat' : 'Enable repeat'}
      {...other}
    >
      <RepeatIcon />
    </StyledButton>
  )
}

const PreviousButton = (props) => {
  const { onClick, disabled, ...other } = props
  return (
    <StyledButton onClick={onClick} tooltipText="Previous" disabled={disabled} {...other}>
      <PreviousIcon />
    </StyledButton>
  )
}

const NextButton = (props) => {
  const { onClick, ...other } = props
  return (
    <StyledButton onClick={onClick} tooltipText="Next" {...other}>
      <NextIcon />
    </StyledButton>
  )
}

interface Props {
  audio: HTMLAudioElement
  isMobile?: boolean
}

const AudioPlaybackControls: FC<Props> = observer(({ audio, isMobile }) => {
  const {
    isPlaying,
    isRepeat,
    isAutoplay,
    toggleAutoplay,
    onPlayPause,
    onStop,
    onRepeat,
    playNext,
    playPrevious,
    previousDisabled,
    ...other
  } = usePlaybackControls(audio)

  if (isMobile) {
    return (
      <PlayPauseButton
        onClick={(evt) => {
          evt.stopPropagation()
          segmentGlobalPlayer(isPlaying ? 'pause' : 'play')
          onPlayPause()
        }}
        isPlaying={isPlaying}
        marginRight
      />
    )
  }
  return (
    <Row css={{ paddingBottom: '4px' }} verticalCenter horizontalCenter>
      {/* <ShuffleButton onClick={() => {}} isShuffle={false} marginRight /> */}
      <AutoplayButton
        onClick={() => {
          segmentGlobalPlayer(isAutoplay ? 'disable autoplay' : 'enable autoplay')
          toggleAutoplay()
        }}
        isAutoplay={isAutoplay}
        marginRight
      />
      <PreviousButton
        onClick={() => {
          segmentGlobalPlayer(previousDisabled ? 'invalid play previous' : 'play previous')
          playPrevious()
        }}
        disabled={previousDisabled}
        marginRight
      />
      <PlayPauseButton
        onClick={() => {
          segmentGlobalPlayer(isPlaying ? 'pause' : 'play')
          onPlayPause()
        }}
        isPlaying={isPlaying}
      />
      <NextButton
        onClick={() => {
          segmentGlobalPlayer('play next')
          playNext()
        }}
        marginLeft
      />
      <LoopButton
        onClick={() => {
          segmentGlobalPlayer(isRepeat ? 'disable repeat' : 'enable repeat')
          onRepeat()
        }}
        isRepeat={isRepeat}
        marginLeft
      />
    </Row>
  )
})

interface PlayerControlsSectionProps {
  isMobile?: boolean
}

const PlayerControlsSection: FC<PlayerControlsSectionProps> = ({ isMobile }) => {
  const { globalPlayer } = useTreeState()

  if (isMobile) return <AudioPlaybackControls audio={globalPlayer.getAudio()} isMobile={isMobile} />
  return (
    <PlayerSection>
      <ControlsContainer>
        <AudioPlaybackControls audio={globalPlayer.getAudio()} />
        <SampleTimelineControls audio={globalPlayer.getAudio()} />
      </ControlsContainer>
    </PlayerSection>
  )
}

export default observer(PlayerControlsSection)
